// TextControl.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Control } from '../ControlRenderer';
import { evaluateExpression, get, set } from '../utils/Utils'
import { FormControl, TextField, Typography, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FormModeType } from '@/components/FormDataProvider';
import { useValidation } from '@/components/ValidationProvider';
import ControlIcons from '../ControlIcons';

interface TextControl extends Control {
  placeholder?: string;
  expression?: string;
  //initValue?: string;
  /**  * Whether to hide the tooltip for the text.
   */
  hideTooltip?: boolean;
}

interface TextControlProps {
  control: Control;
  data: any;
  onDataChange: (newData: any) => void;
  mode: FormModeType;
}

const TextControl: React.FC<TextControlProps> = ({ control, data, onDataChange, mode }) => {
  const { errors, warnings, rule, validateData, setDataContext } = useValidation();
  const theme = useTheme();
  const dataType = useMemo(() => {
    return rule?.properties && control.dataKey ? rule.properties[control.dataKey]?.type : undefined;
  }, [rule, control.dataKey]);

  const handleFieldChange = (fieldName: string, value: string) => {
    let convertedValue: any = value;

    if (value === '') {
      // Treat empty as null by default
      setSelectedValue(null);
      set(data, fieldName, null);
      onDataChange(data);
    }
    // Check if the value should be an integer
    else if (dataType === "integer") {
      if (/^-?\d*$/.test(value)) {
        setSelectedValue(value);
        convertedValue = value === '' ? null : parseInt(value);
        if (!isNaN(convertedValue)) {
          set(data, fieldName, convertedValue);
          onDataChange(data);
        }
      }
    }
    // Check if the value should be a number (integer or float)
    else if (dataType === "number") {
      if (/^-?\d*(\.\d*)?$/.test(value)) {
        setSelectedValue(value);
        convertedValue = value === '' ? null : parseFloat(value);
        if (!isNaN(convertedValue)) {
          set(data, fieldName, convertedValue);
          onDataChange(data);
        }
      }
    }
    // If no specific validation, accept any value
    else {
      setSelectedValue(value);
      set(data, fieldName, value);
      onDataChange(data);
    }
  };

  const [selectedValue, setSelectedValue] = useState<any>(get(data, control.dataKey ?? '', '') ?? '');

  const [isFocused, setIsFocused] = useState(false);

  const errorMessage = errors && control.dataKey ? errors[control.dataKey] : undefined;
  const warningMessage = warnings && control.dataKey ? warnings[control.dataKey] : undefined;

  const hasControlIcons = !!control.helpText || !!control.helpLink || !!errorMessage || !!warningMessage;

  const textControl = control as TextControl;

  const isNumericDataType = dataType === "integer" || dataType === "number";

  const handleBlur = () => {
    setIsFocused(false);

    if (isNumericDataType) {
      setSelectedValue(get(data, control.dataKey ?? '', '') ?? '');
    }

    validateData();
  };

  useEffect(() => {
    if (textControl.expression) {
      try {
        const result = evaluateExpression(textControl.expression, data);
        setSelectedValue(result);
      } catch (error: any) {
        setSelectedValue(error.message);
        console.error('Error evaluating expression:', error);
      }
    } else {
      if (isFocused && isNumericDataType) {
        return;
      }

      setSelectedValue(get(data, control.dataKey ?? '', '') ?? '');
    }
  }, [data, control.dataKey, textControl.expression, errors, onDataChange, isFocused, isNumericDataType]);

  useEffect(() => {
    if (data) {
      setDataContext(data);
    }
  }, [data, setDataContext]);

  /* useEffect(() => {
    if (textControl.initValue !== undefined && control.dataKey) {
      set(data, control.dataKey, null);
    }
  }, [data, control.dataKey, textControl.initValue]); */

  let currentMode = control.mode ? control.mode : mode;

  if (currentMode === 'label') {
    return (
      <Typography className='eform-labelmode'>
        {selectedValue}
      </Typography>
    )
  }

  const className = `eform-text-${currentMode === 'view' ? 'readonly-' : ''}${theme.palette.mode}`;
  const style = control.minWidth ? { minWidth: control.minWidth } : {};
  const hideTooltip = textControl.hideTooltip ?? false;

  return (
    <Tooltip
      title={selectedValue && !isFocused && !hideTooltip ? selectedValue.toString() : ''}
      PopperProps={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, -10], // move closer to the item
            },
          },
        ],
      }}
      arrow>
      {hasControlIcons ? (
        <FormControl variant="standard" fullWidth error={!!errorMessage}><TextField
          variant="standard"
          type="text"
          className={className}
          fullWidth
          error={!!errorMessage}
          //label={control.label}
          //name={control.name}
          value={selectedValue}
          placeholder={textControl.placeholder}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          onChange={(e) => handleFieldChange(control.dataKey ?? '', e.target.value)}
          style={style}
          inputProps={{
            readOnly: currentMode === 'view',
            spellCheck: true,
            //pattern: validationRule?.type === "integer" ? "\\d*" : validationRule?.type === "number" ? "\\d*\\.?\\d*" : undefined // Conditionally add pattern attribute
          }}
        />
        <ControlIcons control={control} errorMessage={errorMessage} warningMessage={warningMessage} />
        </FormControl>
      ) : (
        <TextField
          variant="standard"
          type="text"
          className={className}
          fullWidth
          value={selectedValue}
          placeholder={textControl.placeholder}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          onChange={(e) => handleFieldChange(control.dataKey ?? '', e.target.value)}
          style={style}
          inputProps={{
            readOnly: !!textControl.expression || currentMode === 'view',
            spellCheck: true
          }}
        />)}
    </Tooltip>
  );
};

export default TextControl;
